import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import '../css/Plot.css';

import { ChartOptions } from 'chart.js';

// Icons
import { TbZoomReset, TbZoomIn, TbZoomOut } from "react-icons/tb";
import { MdOutlineFileUpload } from "react-icons/md";

// Assets
const FNNlogo = require('../../pages/HeaderFolder/img/FNNLogoWithBlackFont-06.png');


ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin
);

type FileInfo = {
  date: string;
  receiver: string;
  location: { latitude: number; longitude: number };
  software: string;
  'PPS Hns': number;
  'Initial HNSTS'?: number;
  'Final HNSTS'?: number;
  'Initial SOD'?: number;
  'Final SOD'?: number;
  Decimated: boolean;
  'Sample Rate': number;
  'GPS Valid'?: boolean;
};

const Plot: React.FC = () => {
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
  const [plotData, setPlotData] = useState<{ x: number[]; y: number[] } | null>(null);
  const chartRef = useRef<any>(null);
  const location = useLocation();
  const [filename, setFilename] = useState<string>('');
  const isPlotPage = location.pathname.endsWith('/plot');

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const filepath = queryParams.get('filepath');
    const source = queryParams.get('source');

    if (filepath && source) {
      const decodedFilePath = decodeURIComponent(filepath);


      // Extract filename from decodedFilePath
      const parts = decodedFilePath.split('/');
      const extractedFilename = parts.length > 0 ? parts[parts.length - 1] : '';
      setFilename(extractedFilename);

      // Call your fetch logic with this path and source
      fetchFileFromBackend(decodedFilePath, source);

      // Remove query parameters from the URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location.search]);

  const resetZoom = () => {
    if (chartRef.current) {
      chartRef.current.resetZoom();
    }
  };

  const zoomIn = () => {
    if (chartRef.current) {
      chartRef.current.zoom(1.2); // Zoom in by 20%
    }
  };

  const zoomOut = () => {
    if (chartRef.current) {
      chartRef.current.zoom(0.8); // Zoom out by 20%
    }
  };

  const fetchFileFromBackend = async (path: string, origin: string) => {
    try {
      const response = await fetch(`/apidet/fnnbackend/getFileToPlot?origin=${origin}&path=${path}`, {
        method: 'GET',
        credentials: "same-origin"
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const arrayBuffer = await response.arrayBuffer();
      const dummyFile = new File([arrayBuffer], path.split('/').pop() || 'uploaded.bin');

      const dummyEvent = {
        target: {
          files: [dummyFile],
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      await handleFileUpload(dummyEvent);

    } catch (error) {
      console.error("Error fetching file:", error);
    }
  };

  const fetchBinFile = async () => {
    const origin = 'dataUSA';
    const path = '/home/teamdetector/DetectorServer/data/Bunnell/2025-05-20/2025-05-20-14-00-20-616115250_HF.bin';

    try {
      const response = await fetch(`/apidet/fnnbackend/getFileToPlot?origin=${origin}&path=${path}`, {
        method: 'GET',
        credentials: "same-origin"
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const arrayBuffer = await response.arrayBuffer();

      const dummyFile = new File([arrayBuffer], "2025-05-20-14-00-20-616115250_HF.bin");

      // Manually call existing logic
      const dummyEvent = {
        target: {
          files: [dummyFile],
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      await handleFileUpload(dummyEvent);


      await handleFileUpload(dummyEvent);

    } catch (error) {
      console.error("Error fetching file:", error);
    }
  };


  const parseHeader = (headerString: string) => {
    const header = headerString.split(',');

    try {
      if (header.length <= 5) {
        const [MAC, LAT, LONG, SW, PPSHns] = header;
        return {
          MAC,
          LAT: parseInt(LAT),
          LONG: parseInt(LONG),
          SW,
          PPSHns: parseInt(PPSHns)
        };
      } else {
        const [MAC, LAT, LONG, SW, PPSHns, INIT, END, SOD0, SOD1, GPSVALID, S_RATE] = header;
        return {
          MAC,
          LAT: parseInt(LAT),
          LONG: parseInt(LONG),
          SW,
          PPSHns: parseInt(PPSHns),
          INIT,
          END: parseInt(END),
          SOD0: parseInt(SOD0),
          SOD1: parseInt(SOD1),
          GPSVALID: GPSVALID === 'true',
          S_RATE: S_RATE ? parseFloat(S_RATE) : null
        };
      }
    } catch (err) {
      console.log(`No Header information available: ${err}`);
      return null;
    }
  };

  const convertLatLong = (lat: number, long: number) => {
    const longSign = ((long >> 31) & 1) ? -1 : 1;
    const longDegrees = (long >> 23) & 0xFF;
    const longMinutes = (long >> 17) & 0x3F;
    const longFraction = long & 0x1FFFF;
    const longitude = longSign * (longDegrees + (longMinutes + longFraction / 100000) / 60);

    const latSign = ((lat >> 31) & 1) ? -1 : 1;
    const latDegrees = (lat >> 23) & 0xFF;
    const latMinutes = (lat >> 17) & 0x3F;
    const latFraction = lat & 0x1FFFF;
    const latitude = latSign * (latDegrees + (latMinutes + latFraction / 100000) / 60);

    return { latitude, longitude };
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const arrayBuffer = await file.arrayBuffer();
    const dataView = new DataView(arrayBuffer);

    try {
      const headerLength = dataView.getUint32(0, true); // Little-endian
      const headerBytes = new Uint8Array(arrayBuffer.slice(4, 4 + headerLength));
      const headerString = new TextDecoder().decode(headerBytes);
      const header = parseHeader(headerString);
      if (!header) return;

      const lat = header.LAT;
      const long = header.LONG;
      const location = convertLatLong(lat, long);
      const filename = file.name;
      const isLF = filename.includes('LF');
      const dateString = filename.split('_')[0]?.slice(0, 19);
      const timestamp = dateString.replace(/-/g, ':').replace('T', ' ');

      const INIT = header.INIT ? parseInt(header.INIT) : 0;
      const END = header.END ?? 0;
      const PPSHns = header.PPSHns ?? 20_000_000; // fallback if missing
      const decimated = isLF; // LF implies decimation
      const sampleRate = header.S_RATE || (isLF ? 250_000 : 5_000_000);

      const info: FileInfo = {
        date: timestamp,
        receiver: header.MAC,
        location,
        software: header.SW,
        'PPS Hns': PPSHns,
        'Initial HNSTS': INIT * 50,
        'Final HNSTS': END * 50,
        'Initial SOD': header.SOD0,
        'Final SOD': header.SOD1,
        Decimated: decimated,
        'Sample Rate': sampleRate,
        'GPS Valid': header.GPSVALID
      };

      setFileInfo(info);

      const dataStart = 4 + headerLength;
      const fileSize = arrayBuffer.byteLength;
      const dataBuffer = arrayBuffer.slice(dataStart);

      const headerFields = headerString.split(',').length;
      let ADC: number[] = [];
      let ts: number[] = [];

      if (headerFields > 5) {
        // New firmware
        if (isLF) {
          const floatArray = fileSize >= 40000
            ? new Float64Array(dataBuffer)
            : new Float32Array(dataBuffer);

          // ADC adjustment
          ADC = Array.from(floatArray, val => (val - 2048) / 4);

          // Timestamp logic for LF
          const sampleIntervalNs = ((END * 50 + 1_000_000_000 - INIT * 50) / ADC.length);
          const meanNsWindow = sampleIntervalNs / 2;
          const startNs = INIT * 50;

          ts = ADC.map((_, i) => {
            const hns = i * sampleIntervalNs + startNs + meanNsWindow;
            const corr = 2e-2 / (PPSHns - 1);
            return parseFloat((hns * corr).toFixed(5));
          });

        } else {
          // HF
          const uint16Array = new Uint16Array(dataBuffer);
          ADC = Array.from(uint16Array, val => (val - 2048) / 4);

          const sampleIntervalNs = (1 / sampleRate) * 1_000_000_000;
          const startNs = INIT * 50;

          ts = ADC.map((_, i) => {
            const hns = i * sampleIntervalNs + startNs;
            const corr = 2e-2 / (PPSHns - 1);
            return parseFloat((hns * corr).toFixed(5));
          });

        }

      } else {
        // Old firmware
        const uint32Array = new Uint32Array(dataBuffer);
        const packet1 = uint32Array.filter((_, idx) => idx % 2 === 0); // even
        const HNStsRaw = uint32Array.filter((_, idx) => idx % 2 === 1); // odd

        const corr = 1 / (PPSHns - 1);
        const HNSts = HNStsRaw.map(hns => Math.floor(hns * corr));

        ADC = Array.from(packet1, val => ((val & 0xFFF) - 2048) / 4);
        const SOD = Array.from(packet1, val => (val >> 12) & 0x1FFFF);
        ts = Array.from(SOD, (sod, i) => HNSts[i] + sod - SOD[0]);

      }

      setPlotData({ x: ts, y: ADC });

    } catch (err) {
      console.log(`Could not parse file: ${err}`);
    }
  };



  const chartOptions = useMemo<ChartOptions<'line'>>(() => ({
    responsive: true,
    scales: {
      x: {
        title: { display: true, text: 'Time (s)' },
        ticks: { maxTicksLimit: 8 }
      },
      y: {
        title: { display: true, text: 'ADC' }
      }
    },
    plugins: {
      zoom: {
        pan: {
          enabled: true,
          mode: 'x',
          modifierKey: 'ctrl'
        },
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          drag: {
            enabled: true,
            borderColor: 'rgba(40, 170, 225, 0.5)',
            borderWidth: 1,
            backgroundColor: 'rgba(40, 170, 225, 0.15)',
          },
          mode: 'xy' // rectangle zoom in both directions
        }
      }
    }
  }), []);



  return (
    <div className="plot-overall-wrapper">

      {/* <button onClick={() => fetchBinFile()}>
        Fetch BIN file from backend
      </button> */}

      {plotData && (
        <div className="plot-chart">
          <div className='plot-and-zoom'>
            <h3 className='plot-h3'> Signal Plot </h3>
            <div className='plot-zoom-options'>
              <button onClick={zoomIn} className='resetZoom-button'>
                <TbZoomIn className='plot-zoom-icon' />
              </button>
              <button onClick={zoomOut} className='resetZoom-button'>
                <TbZoomOut className='plot-zoom-icon' />
              </button>
              <button onClick={resetZoom} className='resetZoom-button'>
                <TbZoomReset className='plot-zoom-icon' />
                Reset Zoom
              </button>
              <label className='resetZoom-button' style={{ cursor: 'pointer' }}>
                <MdOutlineFileUpload className='plot-zoom-icon' />
                Upload
                <input
                  type="file"
                  accept=".bin"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </div>

          <Line
            ref={chartRef}
            data={{
              labels: plotData.x,
              datasets: [
                {
                  label: 'ADC vs Time (s)',
                  data: plotData.y,
                  borderColor: '#28aae1',
                  borderWidth: 1,
                  pointRadius: 0
                }
              ]
            }}
            options={chartOptions}
          />

          <p><strong>Hint</strong>: Make a square with your mouse for a more targeted zoom</p>

        </div>
      )}

      {fileInfo && (
        <div className='plot-list-and-title'>
          <h3 className='plot-h3'>Signal Information</h3>
          <p>File Name: {filename}</p>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <ul className="plot-list">
              {/* GPS Valid First */}
              {'GPS Valid' in fileInfo && (
                <li key="GPS Valid">
                  <strong className="plot-key">GPS Valid:</strong>
                  <div
                    className='plot-card-value'
                    style={{ color: fileInfo['GPS Valid'] === false ? 'red' : 'inherit' }}
                  >
                    {fileInfo['GPS Valid']?.toString()}
                  </div>
                </li>
              )}

              {/* All Other Keys */}
              {Object.entries(fileInfo).map(([key, value]) => {
                if (key === 'GPS Valid') return null; // Already handled above

                if (key === 'location' && typeof value === 'object' && value !== null) {
                  return (
                    <React.Fragment key={key}>
                      <li><strong className="plot-key">Latitude: </strong>
                        <div className='plot-card-value'>{value.latitude.toFixed(5)}</div>
                      </li>
                      <li><strong className="plot-key">Longitude: </strong>
                        <div className='plot-card-value'>{value.longitude.toFixed(5)}</div>
                      </li>
                    </React.Fragment>
                  );
                }

                return (
                  <li key={key}>
                    <strong className="plot-key">{key.charAt(0).toUpperCase() + key.slice(1)}:</strong>
                    <div className='plot-card-value'>
                      {typeof value === 'object' ? JSON.stringify(value) : value?.toString()}
                    </div>
                  </li>
                );
              })}
            </ul>

          </div>
        </div>
      )}


      {!filename && !fileInfo && (
        <div style={{ marginBottom: '2rem', marginTop: '2rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <h3>Upload a bin file</h3>
          <label style={{ cursor: 'pointer' }}>
            <MdOutlineFileUpload className='upload-plot-icon' />
            <input
              type="file"
              accept=".bin"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </label>

        </div>
      )}

      {isPlotPage && (
        <a href="/" className="logo-container">
          <img src={FNNlogo} className="plot-log" alt="FNN logo" />
        </a>
      )}


    </div>


  );

};

export default Plot;
